import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Table, Button, Modal, Form, Badge, Spinner } from 'react-bootstrap';
import DrawioEditor, { DrawioEditorHandle } from './DrawioEditor';
import { useProject } from '../../../contexts/ProjectContext';
import ApiService from 'helpers/apiService';

/**
 * DrawioDiagramsPage — свободный редактор схем на встроенном drawio.
 * Дополняет структурированный swimlane-редактор (/diagrams) — здесь произвольные
 * блок-схемы/архитектурные диаграммы. Тот же DrawioEditor переиспользует wiki.
 *
 * Хранение (DRAWIO-1): admin-api /diagrams/v1/drawio (per-project через X-Project-ID).
 * xml — редактируемый исходник; svg-рендер (для превью/wiki) — DRAWIO-3 (бэклог).
 */

interface DrawioMeta {
  id: string;
  name: string;
  updated_at: string;
}

interface DrawioFull {
  id: string;
  name: string;
  xml: string;
}

const DrawioDiagramsPage: React.FC = () => {
  const { currentProjectId } = useProject() as { currentProjectId?: string | null };
  const [list, setList] = useState<DrawioMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<DrawioFull | null>(null);
  const [renameTarget, setRenameTarget] = useState<DrawioMeta | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const editorRef = useRef<DrawioEditorHandle>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await ApiService.drawioList();
      setList(Array.isArray(items) ? items : []);
    } catch (e) {
      setError('Не удалось загрузить список схем');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Перезагрузка при смене проекта (X-Project-ID меняется в interceptor).
  useEffect(() => {
    reload();
  }, [reload, currentProjectId]);

  const handleCreate = useCallback(async () => {
    const name = nameInput.trim();
    if (!name) return;
    setBusy(true);
    try {
      const id = await ApiService.drawioCreate({ name, xml: '' });
      setShowCreate(false);
      setNameInput('');
      setEditing({ id, name, xml: '' });
      reload();
    } catch {
      setError('Не удалось создать схему');
    } finally {
      setBusy(false);
    }
  }, [nameInput, reload]);

  const openEditor = useCallback(async (item: DrawioMeta) => {
    setBusy(true);
    try {
      const full = await ApiService.drawioGet(item.id);
      setEditing({ id: full.id, name: full.name, xml: full.xml || '' });
    } catch {
      setError('Не удалось открыть схему');
    } finally {
      setBusy(false);
    }
  }, []);

  // autosave/save из drawio → PUT xml. editing.xml держим в синхроне.
  const handleSaveXml = useCallback(
    (xml: string) => {
      if (!editing) return;
      ApiService.drawioUpdate(editing.id, { name: editing.name, xml }).catch(() => {
        setError('Не удалось сохранить изменения');
      });
      setEditing((prev) => (prev ? { ...prev, xml } : prev));
    },
    [editing]
  );

  const handleDelete = useCallback(
    async (item: DrawioMeta) => {
      if (!window.confirm(`Удалить схему «${item.name}»?`)) return;
      try {
        await ApiService.drawioDelete(item.id);
        reload();
      } catch {
        setError('Не удалось удалить схему');
      }
    },
    [reload]
  );

  // Переименование сохраняет xml (фетчим текущий, чтобы PUT его не затёр).
  const handleRename = useCallback(async () => {
    if (!renameTarget) return;
    const name = nameInput.trim();
    if (!name) return;
    setBusy(true);
    try {
      const full = await ApiService.drawioGet(renameTarget.id);
      await ApiService.drawioUpdate(renameTarget.id, { name, xml: full.xml || '' });
      setRenameTarget(null);
      setNameInput('');
      reload();
    } catch {
      setError('Не удалось переименовать схему');
    } finally {
      setBusy(false);
    }
  }, [renameTarget, nameInput, reload]);

  const handleExport = useCallback(
    async (format: 'xmlsvg' | 'xmlpng') => {
      if (!editorRef.current) return;
      const res = await editorRef.current.exportDiagram(format);
      const a = document.createElement('a');
      a.href = res.data;
      a.download = (editing?.name || 'diagram') + (format === 'xmlsvg' ? '.svg' : '.png');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    [editing]
  );

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('ru-RU');
    } catch {
      return iso;
    }
  };

  // Полноэкранный редактор
  if (editing) {
    return (
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{editing.name}</strong>
            <Badge bg="light" text="dark" className="ms-2 small">
              drawio
            </Badge>
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => handleExport('xmlsvg')}>
              Экспорт SVG
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => handleExport('xmlpng')}>
              Экспорт PNG
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
              Закрыть
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-2">
          <DrawioEditor
            ref={editorRef}
            xml={editing.xml}
            title={editing.name}
            onSave={handleSaveXml}
            onExit={() => setEditing(null)}
            height="78vh"
          />
          <div className="text-muted small mt-2">
            Изменения сохраняются автоматически (autosave) и по кнопке «Сохранить» в редакторе.
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">Схемы (drawio)</h5>
            <div className="text-muted small">
              Свободный редактор блок-схем и архитектурных диаграмм. Структурированные
              user-journey сценарии — в разделе «Диаграммы процессов».
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={reload} disabled={loading}>
              Обновить
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                setNameInput('');
                setShowCreate(true);
              }}
            >
              Новая схема
            </Button>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <div className="alert alert-warning py-2 small" role="alert">
          {error}
        </div>
      )}

      <Card>
        <Card.Body>
          <Table hover responsive size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Название</th>
                <th>Обновлено</th>
                <th className="text-end">Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Загрузка…
                  </td>
                </tr>
              )}
              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    Схем пока нет. Нажмите «Новая схема».
                  </td>
                </tr>
              )}
              {!loading &&
                list.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Button variant="link" className="p-0" onClick={() => openEditor(d)} disabled={busy}>
                        {d.name}
                      </Button>
                    </td>
                    <td className="text-muted small">{fmtDate(d.updated_at)}</td>
                    <td className="text-end">
                      <div className="d-flex gap-1 justify-content-end">
                        <Button size="sm" variant="outline-secondary" onClick={() => openEditor(d)} disabled={busy}>
                          Открыть
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setRenameTarget(d);
                            setNameInput(d.name);
                          }}
                        >
                          Ред.
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(d)}>
                          Del
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Модал создания */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Новая схема</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="small">Название</Form.Label>
            <Form.Control
              size="sm"
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="Напр. Поток регистрации"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setShowCreate(false)}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" onClick={handleCreate} disabled={!nameInput.trim() || busy}>
            Создать
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модал переименования */}
      <Modal show={!!renameTarget} onHide={() => setRenameTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Переименовать</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label className="small">Название</Form.Label>
            <Form.Control
              size="sm"
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setRenameTarget(null)}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" onClick={handleRename} disabled={!nameInput.trim() || busy}>
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DrawioDiagramsPage;
